import { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { ThemeContext } from 'styled-components';

import avatar from 'assets/images/avatar.png';
import { status } from 'constants/general';
import postsStrings from 'modules/posts/strings';
import {
  fileUploaderIsolatedFileTypeEnum,
  staticDataLanguageDetailsIdEnum,
} from 'modules/common/enums';
import Loader from 'modules/common/components/Loader';
import MultiSelectionDropdownMenu from 'modules/common/components/MultiSelectionDropdownMenu';
import Checkbox from 'modules/common/components/Checkbox';
import FileUploaderIsolated from 'modules/common/components/FileUploaderIsolated';
import Popup from 'modules/common/components/Popup';
import SectionDivider from 'modules/common/components/SectionDivider';

import {
  LoaderWrapper,
  RssFeedWrapper,
  FormWrapper,
  FormTitle,
  FormGrid,
  FormLabel,
  FormTextInput,
  FormFieldWrapper,
  FormMedia,
  FormValidationError,
  FormUploaderErrorText,
  FormUploaderBtnWrapper,
  FormUploaderBtn,
  FormBtnWrapper,
  FormBtn,
  /* ####################### */
  FileItemWrapper,
  FileItemPadding,
  FileImg,
  ResizedFileImg,
  VideoIcon,
  ImgDeleteOverlay,
  ImgDeleteIcon,
  FileItem,
  ImgPreview,
} from './AddRssFeedComponent.style';

const MediaFile = ({
  index,
  fileData,
  isPreviewFilePopupOpen,
  previewFileUrl,
  handlePreviewFileClick,
  handleDeleteFileIconClick,
}) => {
  const { fileTypeId, dataUrl, isProcessed, dimensions } = fileData;
  const { dimenParamToChange, ratio } = dimensions;

  const imgSrc = dataUrl || avatar;
  let fileOpacity = (isProcessed && 0.6) || 0.5;
  let fileBlur = (isProcessed && 3) || 0;
  if (fileTypeId === fileUploaderIsolatedFileTypeEnum.video) {
    fileOpacity = 1;
    fileBlur = 0;
  }

  return (
    <>
      <FileItemWrapper>
        <FileItem onClick={() => handlePreviewFileClick(true, fileData)}>
          <FileItemPadding />
          <FileImg src={imgSrc} opacity={fileOpacity} blur={fileBlur} />
          {fileTypeId === fileUploaderIsolatedFileTypeEnum.image && (
            <ResizedFileImg src={imgSrc} dimenParamToChange={dimenParamToChange} ratio={ratio} />
          )}
          {fileTypeId === fileUploaderIsolatedFileTypeEnum.video && isProcessed && <VideoIcon />}
          <ImgDeleteOverlay />
          <ImgDeleteIcon onClick={event => handleDeleteFileIconClick(event, index)} />
        </FileItem>
      </FileItemWrapper>
      {isPreviewFilePopupOpen && (
        <Popup handleCloseBtnClick={() => handlePreviewFileClick(false, fileData)}>
          <ImgPreview src={previewFileUrl} />
        </Popup>
      )}
    </>
  );
};
MediaFile.propTypes = {
  index: PropTypes.number.isRequired,
  fileData: PropTypes.shape({
    fileTypeId: PropTypes.number,
    dataUrl: PropTypes.string,
    shouldBeProcessed: PropTypes.bool,
    isProcessed: PropTypes.bool,
    dimensions: PropTypes.shape({
      dimenParamToChange: PropTypes.string,
      ratio: PropTypes.number,
    }),
  }).isRequired,
  isPreviewFilePopupOpen: PropTypes.bool.isRequired,
  previewFileUrl: PropTypes.string.isRequired,
  handlePreviewFileClick: PropTypes.func.isRequired,
  handleDeleteFileIconClick: PropTypes.func.isRequired,
};

/* ############################################################################### */
/* ############################################################################### */
/* ############################################################################### */

const RssFeedComponent = ({
  formFieldsValues,
  formFieldsValidation,
  uploadMediaErrorMessage,
  shouldShowErrorMessage,
  isPreviewFilePopupOpen,
  previewFileUrl,
  handleTextInputFieldChange,
  handleMultiSelectionDropdownFieldChange,
  handleCheckboxFieldChange,
  handleUploadFileChange,
  handleUploadFileErrorChange,
  handlePreviewFileClick,
  handleDeleteFileIconClick,
  handleAddRssFeedBtnClick,
}) => {
  const { staticData, companyProfilesList, addRssFeed } = useSelector(state => ({
    staticData: state.common.staticData,
    companyProfilesList: state.common.companyProfilesList,
    addRssFeed: state.posts.addRssFeed,
  }));

  const topicsDropdownData = useMemo(
    () =>
      staticData.details?.interests?.map(interest => ({
        key: interest.interestKey,
        name: interest.languageDetails.find(
          languageDetail => languageDetail.languageId === staticDataLanguageDetailsIdEnum.english,
        ).name,
      })),
    [staticData],
  );

  // const readImageCheckboxObj = {
  //   key: 'readImage',
  //   name: postsStrings.addRssFeed.formReadImageFieldText,
  // };

  const forceOpenGraphCheckboxObj = {
    key: 'forceOpenGraph',
    name: postsStrings.addRssFeed.formForceOpenGraphFieldText,
  };

  const themeContext = useContext(ThemeContext);
  const { colors } = themeContext;

  if (
    staticData.status === status.SHOULD_CALL_API ||
    staticData.status === status.FETCHING ||
    companyProfilesList.status === status.SHOULD_CALL_API ||
    companyProfilesList.status === status.FETCHING
  )
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );

  return (
    <RssFeedWrapper>
      <FormWrapper>
        <FormTitle>{postsStrings.addRssFeed.formTitle}</FormTitle>
        <SectionDivider gap={16} withSeparator separatorGap={16} borderColor={colors.grey004} />
        <FormGrid>
          {/* Link Field */}
          <FormFieldWrapper span={2}>
            <FormLabel>{postsStrings.addRssFeed.formLinkFieldLabel}</FormLabel>
            <SectionDivider gap={8} />
            <FormTextInput
              type="text"
              value={formFieldsValues.link}
              onChange={event => handleTextInputFieldChange('link', event)}
            />
            {formFieldsValidation.link === false && (
              <>
                <SectionDivider gap={4} />
                <FormValidationError>
                  {postsStrings.addRssFeed.formFieldErrorText(
                    postsStrings.addRssFeed.formLinkFieldLabel,
                  )}
                </FormValidationError>
              </>
            )}
          </FormFieldWrapper>

          {/* Frequency Field */}
          {/* <FormFieldWrapper>
            <FormLabel>{postsStrings.addRssFeed.formFrequencyFieldLabel}</FormLabel>
            <SectionDivider gap={8} />
            <FormTextInput
              type="number"
              value={formFieldsValues.frequency}
              onChange={event => handleTextInputFieldChange('frequency', event)}
            />
          </FormFieldWrapper> */}

          {/* Read Image Field */}
          {/* <FormFieldWrapper>
            <FormLabel>{postsStrings.addRssFeed.formReadImageFieldLabel}</FormLabel>
            <SectionDivider gap={13} />
            <Checkbox
              checked={formFieldsValues.readImage}
              checkboxObj={readImageCheckboxObj}
              handleCheckboxClick={() => handleCheckboxFieldChange('readImage')}
            />
          </FormFieldWrapper> */}

          {/* Force OG Field */}
          <FormFieldWrapper>
            <FormLabel>{postsStrings.addRssFeed.formForceOpenGraphFieldLabel}</FormLabel>
            <SectionDivider gap={13} />
            <Checkbox
              checked={formFieldsValues.forceOpenGraph}
              checkboxObj={forceOpenGraphCheckboxObj}
              handleCheckboxClick={() => handleCheckboxFieldChange('forceOpenGraph')}
            />
          </FormFieldWrapper>

          {/* Topic Field */}
          <FormFieldWrapper fullWidth>
            <FormLabel>{postsStrings.addRssFeed.formTopicFieldLabel}</FormLabel>
            <SectionDivider gap={8} />
            <MultiSelectionDropdownMenu
              dropdownMenuItemsList={topicsDropdownData}
              dropdownMenuSelectedArr={formFieldsValues.topic}
              dropdownMenuPlaceholder={postsStrings.addRssFeed.formTopicFieldPlaceholder}
              handleDropdownMenuChange={value =>
                handleMultiSelectionDropdownFieldChange('topic', value)
              }
            />
            {formFieldsValidation.topic === false && (
              <>
                <SectionDivider gap={4} />
                <FormValidationError>
                  {postsStrings.addRssFeed.formFieldErrorText(
                    postsStrings.addRssFeed.formTopicFieldLabel,
                  )}
                </FormValidationError>
              </>
            )}
          </FormFieldWrapper>

          {/* Media Field */}
          <FormFieldWrapper fullWidth>
            <FormLabel>{postsStrings.addRssFeed.formMediaFieldLabel}</FormLabel>
            <SectionDivider gap={25} />
            <FormMedia>
              {formFieldsValues.media.uploadedFiles.map((fileData, index) => (
                <MediaFile
                  key={fileData.id}
                  index={index}
                  fileData={fileData}
                  isPreviewFilePopupOpen={isPreviewFilePopupOpen}
                  previewFileUrl={previewFileUrl}
                  handlePreviewFileClick={handlePreviewFileClick}
                  handleDeleteFileIconClick={handleDeleteFileIconClick}
                />
              ))}
            </FormMedia>
            <SectionDivider gap={15} />
            {formFieldsValidation.media === false && (
              <>
                <FormValidationError centerText>
                  {postsStrings.addRssFeed.formFieldErrorText(
                    postsStrings.addRssFeed.formMediaFieldLabel,
                  )}
                </FormValidationError>
                <SectionDivider gap={4} />
              </>
            )}
            {uploadMediaErrorMessage && (
              <>
                <FormUploaderErrorText>{uploadMediaErrorMessage}</FormUploaderErrorText>
                <SectionDivider gap={15} />
              </>
            )}
            <FileUploaderIsolated
              fileInputAcceptAttrRegex="image/*"
              filesCountLimit={1}
              filesSizeLimitInMB={10}
              indexToPrepend={1}
              uploadedData={formFieldsValues.media}
              setUploadedData={handleUploadFileChange}
              setErrorMessage={handleUploadFileErrorChange}
            >
              <FormUploaderBtnWrapper>
                <FormUploaderBtn as="label" htmlFor={`file-uploader-btn-${1}`}>
                  {postsStrings.addRssFeed.formMediaFieldBtnText}
                </FormUploaderBtn>
              </FormUploaderBtnWrapper>
            </FileUploaderIsolated>
          </FormFieldWrapper>
        </FormGrid>
        {shouldShowErrorMessage === true && (
          <>
            <SectionDivider gap={15} />
            <FormValidationError centerText>
              {postsStrings.addRssFeed.requestErrorMessage}
            </FormValidationError>
          </>
        )}
        <SectionDivider gap={35} />
        <FormBtnWrapper>
          <FormBtn as="button" onClick={handleAddRssFeedBtnClick}>
            {(addRssFeed.status === status.FETCHING && (
              <Loader width={14} height={14} thickness={4} />
            )) ||
              postsStrings.addRssFeed.formAddBtnText}
          </FormBtn>
        </FormBtnWrapper>
      </FormWrapper>
    </RssFeedWrapper>
  );
};

RssFeedComponent.propTypes = {
  formFieldsValues: PropTypes.shape({
    link: PropTypes.string,
    frequency: PropTypes.string,
    topic: PropTypes.arrayOf(PropTypes.shape({})),
    readImage: PropTypes.bool,
    forceOpenGraph: PropTypes.bool,
    media: PropTypes.shape({
      uploadedFiles: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }).isRequired,
  formFieldsValidation: PropTypes.shape({
    link: PropTypes.bool,
    topic: PropTypes.bool,
    media: PropTypes.bool,
  }).isRequired,
  uploadMediaErrorMessage: PropTypes.string.isRequired,
  shouldShowErrorMessage: PropTypes.bool.isRequired,
  isPreviewFilePopupOpen: PropTypes.bool.isRequired,
  previewFileUrl: PropTypes.string.isRequired,
  handleTextInputFieldChange: PropTypes.func.isRequired,
  handleMultiSelectionDropdownFieldChange: PropTypes.func.isRequired,
  handleCheckboxFieldChange: PropTypes.func.isRequired,
  handleUploadFileChange: PropTypes.func.isRequired,
  handleUploadFileErrorChange: PropTypes.func.isRequired,
  handlePreviewFileClick: PropTypes.func.isRequired,
  handleDeleteFileIconClick: PropTypes.func.isRequired,
  handleAddRssFeedBtnClick: PropTypes.func.isRequired,
};

export default RssFeedComponent;
